import { useEffect, useRef, useState, useCallback, memo } from "react";
import { SearchIcon, X } from "lucide-react";
import { ClockIcon } from "@heroicons/react/24/outline";

import { useLocationSearch } from "@/hooks/use-location-search";
import { Input } from "@/components/ui/input";
import { useMapRouteStore } from "@/stores/mapRouteStore";
import { IMAGES } from "../../svgImagesByType";
import type { ImageTypesEnum } from "@/pages/home/types/directionsTypes";

/* =====================
   Types
===================== */
type RecentSearch = {
  id: number;
  label: string;
};

/* =====================
   Constants
===================== */
const POPULAR_SEARCHES: RecentSearch[] = [
  { id: 1, label: "Кабинет 205" },
  { id: 2, label: "Кабинет 214" },
  { id: 3, label: "Кабинет 295" },
];

/* =====================
   Component
===================== */
type Props = {
  onExpandSidebar?: () => void;
};

const SearchInput = memo(function SearchInput({ onExpandSidebar }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const setRoute = useMapRouteStore((s) => s.setRoute);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const { results, debouncedSearch } = useLocationSearch(search);

  // Update highlighted index when results change
  useEffect(() => {
    if (!debouncedSearch) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(results.length > 0 ? 0 : -1);
  }, [results, debouncedSearch]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ===== Actions ===== */
  const applySearch = useCallback(
    (value: string) => {
      if (!value) return;
      setSearch(value);
      setOpen(false);
      setRoute((r) => ({ ...r, end: value }));
      onExpandSidebar?.();
    },
    [setRoute, onExpandSidebar],
  );

  const clearSearch = useCallback(() => {
    setSearch("");
    setOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : 0,
        );
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : results.length - 1,
        );
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (results[highlightedIndex]) {
          e.currentTarget.blur();
          applySearch(results[highlightedIndex].name);
        }
      }

      if (e.key === "Escape") {
        e.currentTarget.blur();
        setOpen(false);
      }
    },
    [open, results, highlightedIndex, applySearch],
  );

  const handleFocus = useCallback(() => {
    setOpen(true);
    setHighlightedIndex(results.length > 0 ? 0 : -1);
  }, [results.length]);

  /* ===================== */
  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Input */}
      <div className="flex items-center bg-[#f5f7fa] rounded-[10px] h-11 px-3 border border-[#e2e6ed] focus-within:border-[#1a5fb4] focus-within:shadow-[0_0_0_3px_rgba(26,95,180,0.15)] transition-all">
        <SearchIcon className="h-[18px] w-[18px] text-[#8b95a5] shrink-0" />

        <Input
          value={search}
          onFocus={handleFocus}
          onClick={handleFocus}
          onChange={(e) => {
            setOpen(true);
            setSearch(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Поиск кабинета..."
          className="flex-1 h-full bg-transparent border-none px-3 text-sm text-[#1a2138] placeholder:text-[#8b95a5] focus-visible:ring-0 focus-visible:ring-offset-0"
        />

        {search && (
          <button
            onClick={clearSearch}
            className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <ul
          className="absolute left-0 right-0 mt-2 bg-white rounded-[10px] border border-[#e2e6ed] shadow-lg z-[110] max-h-[300px] overflow-y-auto"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#c4c4c4 transparent",
          }}
        >
          {search ? (
            results.length > 0 ? (
              results.map((place, index) => (
                <li
                  key={`${place.name}-${place.imageType}-${index}`}
                  className={`flex items-center py-2.5 px-4 cursor-pointer transition-colors ${
                    index === highlightedIndex
                      ? "bg-[#1a5fb4]/10 text-[#1a2138]"
                      : "text-[#4a5568] hover:bg-[#f5f7fa]"
                  }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => applySearch(place.name)}
                >
                  <div className="flex items-center gap-2 w-full">
                    {place.imageType && (
                      <span className="text-xs text-[#8b95a5] bg-[#f5f7fa] px-2 py-0.5 rounded">
                        {IMAGES[place.imageType as ImageTypesEnum]}
                      </span>
                    )}
                    <span className="text-sm font-medium">{place.name}</span>
                  </div>
                </li>
              ))
            ) : (
              <li className="text-[#8b95a5] py-3 px-4 text-sm">
                Ничего не найдено по "{search}"
              </li>
            )
          ) : (
            <>
              <div className="px-4 pt-3 pb-2">
                <h3 className="text-xs font-semibold text-[#8b95a5] uppercase tracking-wide">
                  Популярное
                </h3>
              </div>
              {POPULAR_SEARCHES.map((item) => (
                <li
                  key={item.id}
                  onClick={() => applySearch(item.label)}
                  className="flex items-center py-2.5 px-4 cursor-pointer hover:bg-[#f5f7fa] text-[#4a5568] transition-colors"
                >
                  <ClockIcon className="h-4 w-4 mr-2 text-[#8b95a5]" />
                  {item.label}
                </li>
              ))}
            </>
          )}
        </ul>
      )}
    </div>
  );
});

export default SearchInput;
